/**
 * Whether a `<database>-info` read carries any metadata at all.
 *
 * An EMPTY file is how a live sync-wasm replica's `-info` reads back from OPFS when it is reopened
 * (the engine opens it fine), so it means "no saved configuration", exactly like a missing file.
 * Parsing it as JSON threw `SyntaxError: Unexpected end of JSON input` on every reopen of a
 * signed-in web replica: the leader worker's open failed on each attempt, the page's actor open
 * never settled, and a reloaded signed-in tab sat on "Syncing data from cloud..." forever
 * (staging run 35808344941, deck-prepopulated). Malformed non-empty metadata still fails closed.
 */
function hasReplicaMetadata(metadataText: string | null): metadataText is string {
  return metadataText !== null && metadataText.trim() !== "";
}

/** A local SQLite file is not a replica: connect() would bootstrap OVER it. */
export function hasTursoReplicaOrigin(metadataText: string | null): boolean {
  if (!hasReplicaMetadata(metadataText)) {
    return false;
  }
  const metadata: unknown = JSON.parse(metadataText);
  if (typeof metadata !== "object" || metadata === null || Array.isArray(metadata)) {
    throw new Error("Invalid Turso replica metadata; existing database files were preserved.");
  }
  const configuration: unknown = Reflect.get(metadata, "saved_configuration");
  if (configuration == null) {
    return false;
  }
  if (typeof configuration !== "object" || Array.isArray(configuration)) {
    throw new TypeError(
      "Invalid Turso replica configuration; existing database files were preserved.",
    );
  }
  const remoteUrl: unknown = Reflect.get(configuration, "remote_url");
  if (remoteUrl == null) {
    return false;
  }
  if (typeof remoteUrl !== "string" || remoteUrl.length === 0) {
    throw new Error("Invalid Turso replica origin; existing database files were preserved.");
  }
  return true;
}

export function assertTursoBootstrapSafe(
  hasExistingFiles: boolean,
  metadataText: string | null,
): void {
  if (hasExistingFiles && !hasTursoReplicaOrigin(metadataText)) {
    throw new Error(
      "Local study must be moved aside before cloud bootstrap. Existing files were preserved.",
    );
  }
}

/**
 * Turso 0.7.2 stores this configuration in `<database>-info`, then overwrites
 * remote_url during connect(). Check BEFORE opening: a cursor and pending CDC
 * belong to one remote database, not whichever dev stack served today's bundle.
 * This is an endpoint guard, not a server-generation check: replacing a database
 * at the same URL still requires preserving/restoring its replication history.
 */
export function assertTursoReplicaOrigin(metadataText: string | null, requestedUrl: string): void {
  if (!hasReplicaMetadata(metadataText)) {
    return;
  }
  const metadata: unknown = JSON.parse(metadataText);
  if (typeof metadata !== "object" || metadata === null || Array.isArray(metadata)) {
    throw new Error("Invalid Turso replica metadata; existing database files were preserved.");
  }
  const configuration: unknown = Reflect.get(metadata, "saved_configuration");
  if (configuration == null) {
    // Older metadata and local-only guest databases may not have a saved remote.
    return;
  }
  if (typeof configuration !== "object" || Array.isArray(configuration)) {
    throw new TypeError(
      "Invalid Turso replica configuration; existing database files were preserved.",
    );
  }
  const savedUrl: unknown = Reflect.get(configuration, "remote_url");
  if (savedUrl == null) {
    return;
  }
  if (typeof savedUrl !== "string" || savedUrl.length === 0) {
    throw new Error("Invalid Turso replica origin; existing database files were preserved.");
  }
  if (normalizeReplicaUrl(savedUrl) !== normalizeReplicaUrl(requestedUrl)) {
    // Do not include URLs: they may contain credentials or account identifiers.
    throw new Error(
      "This actor replica belongs to a different sync server. Existing data and pending " +
        "writes were preserved. Reconnect to its original dev stack or migrate the replica " +
        "before switching servers.",
    );
  }
}

function normalizeReplicaUrl(value: string): string {
  const url = new URL(value);
  // A root URL's optional slash and default ports are equivalent. Preserve all
  // other path/query information; even localhost and 127.0.0.1 may route differently.
  return url.href;
}

/** All persistent files used by the pinned native SDK, including sidecar-only replicas. */
export const TURSO_REPLICA_FILE_SUFFIXES = [
  "",
  "-wal",
  "-shm",
  "-journal",
  "-changes",
  "-wal-revert",
  "-info",
] as const;
